/**
 * FirmaAdminDrawer — Co-AP-Verwaltung (nur für Firma-Admins).
 * Lädt aktuelle Rolle + Section-Permissions des AP, erlaubt Änderung,
 * speichert via setFirmaUserRole / setFirmaUserPermissions.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Save, Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import type { FirmaRole, AccessLevel } from '@/types'

const SECTIONS = [
  { key: 'stammdaten', label: 'Stammdaten' },
  { key: 'studiengaenge', label: 'Studiengänge' },
  { key: 'noten', label: 'Noten' },
  { key: 'kontakt', label: 'Kontaktdaten' },
  { key: 'vorlesungstermine', label: 'Vorlesungstermine' },
  { key: 'zahlungen', label: 'Zahlungen' },
  { key: 'rechnungen', label: 'Rechnungen' },
  { key: 'ansprechpartner', label: 'Ansprechpartner' },
] as const

const LEVELS: AccessLevel[] = ['none', 'read', 'write']
const LEVEL_LABEL: Record<AccessLevel, string> = {
  none: 'Keine',
  read: 'Lesen',
  write: 'Schreiben',
}

interface Props {
  isOpen: boolean
  onClose: () => void
  apUserId: number
  apName: string
  accountId: string
  currentRole: FirmaRole | null
  currentPermissions: Record<string, AccessLevel>
}

export default function FirmaAdminDrawer({
  isOpen, onClose, apUserId, apName, accountId,
  currentRole, currentPermissions,
}: Props) {
  const qc = useQueryClient()
  const [role, setRole] = useState<FirmaRole>(currentRole ?? 'user')
  const [perms, setPerms] = useState<Record<string, AccessLevel>>(currentPermissions ?? {})

  // Lädt die ECHTEN Permissions vom Backend, sobald der Drawer offen ist.
  // Verhindert Bug, dass der Drawer mit leerem perms-Dict initialisiert wird
  // und dann beim Speichern nur den explizit gesetzten Wert sendet — was
  // alle anderen Sections in der DB nicht überschreibt.
  const { data: loadedPerms, isLoading: permsLoading } = useQuery({
    queryKey: ['firmaUserPermissions', accountId, apUserId],
    queryFn: () => api.getFirmaUserPermissions(accountId, apUserId),
    enabled: isOpen && !!accountId && !!apUserId,
    staleTime: 0,
  })

  // Sync state when drawer (re-)opens with different AP
  useEffect(() => {
    if (isOpen) {
      setRole(currentRole ?? 'user')
    }
  }, [isOpen, apUserId, currentRole])

  // Sync perms once permissions are loaded from backend
  useEffect(() => {
    if (isOpen && loadedPerms?.permissions) {
      setPerms(loadedPerms.permissions)
    }
  }, [isOpen, loadedPerms])

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.setFirmaUserRole(accountId, apUserId, role)
      if (role === 'user') {
        await api.setFirmaUserPermissions(accountId, apUserId, perms)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firmaUsers', accountId] })
      qc.invalidateQueries({ queryKey: ['firmaMe', accountId] })
      onClose()
    },
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
         onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Berechtigungen für {apName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-1">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 pb-4 border-b border-gray-200">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Rolle in dieser Firma</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="role" checked={role === 'admin'}
                     onChange={() => setRole('admin')}
                     className="cursor-pointer" />
              <span className="text-sm">Admin (voller Zugriff)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="role" checked={role === 'user'}
                     onChange={() => setRole('user')}
                     className="cursor-pointer" />
              <span className="text-sm">User</span>
            </label>
          </div>
          {role === 'admin' && (
            <p className="text-xs text-gray-500 mt-2">
              Admins haben automatisch volle Schreibrechte auf alle Bereiche.
            </p>
          )}
        </div>

        {role === 'user' && (
          <div className="space-y-2 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Bereiche</h3>
            {permsLoading && (
              <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
                <Loader2 size={14} className="animate-spin" /> Lade aktuelle Berechtigungen…
              </div>
            )}
            {!permsLoading && SECTIONS.map(s => {
              const isWriteOnAnsprechpartner = s.key === 'ansprechpartner'
              return (
                <div key={s.key} className="flex items-center justify-between py-1">
                  <span className="text-sm text-gray-700">{s.label}</span>
                  <div className="flex gap-1">
                    {LEVELS.map(level => {
                      const disabled = isWriteOnAnsprechpartner && level === 'write'
                      const selected = (perms[s.key] ?? 'none') === level
                      return (
                        <label key={level}
                          className={`px-2.5 py-1 text-xs rounded cursor-pointer transition-colors
                            ${selected ? 'bg-fom-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                            ${disabled ? 'opacity-30 cursor-not-allowed pointer-events-none' : ''}`}
                          title={disabled ? 'Schreibrecht auf Ansprechpartner ist Admin-only' : undefined}>
                          <input type="radio" hidden
                            checked={selected}
                            onChange={() => setPerms({ ...perms, [s.key]: level })}
                            disabled={disabled} />
                          {LEVEL_LABEL[level]}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {saveMutation.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded mb-4">
            Speichern fehlgeschlagen. Bitte versuche es erneut.
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-transparent border-none cursor-pointer">
            Abbrechen
          </button>
          <button onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 text-sm bg-fom-500 text-white rounded inline-flex items-center gap-1.5 hover:bg-fom-600 transition-colors disabled:opacity-50 border-none cursor-pointer">
            <Save size={14} />
            {saveMutation.isPending ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )
}
